import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { getMonthlyPayerPaymentSummary } from "store/actions";
import dayjs from "dayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { barOptions, processRawData, transformDataForGrid } from "./barHelper";
import CustomDataGrid from "components/tables/CustomDataGrid";

// Register plugins
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin,
  ChartDataLabels
);

const BillingReport = () => {
  const [barData, setBarData] = useState({});
  const [barAnnotation, setBarAnnotation] = useState(null);
  const [dateTo, setDateTo] = useState(dayjs());
  const [monthRange, setMonthRange] = useState(6); // Default to 6 months
  const [isLoading, setIsLoading] = useState(true);
  const [showValues, setShowValues] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [rawData, setRawData] = useState({ columns: [], rows: [] });
  const theme = useTheme();
  const [dateFrom, setDateFrom] = useState(
    dayjs().subtract(monthRange, "month")
  );

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    setDateFrom(dayjs(dateTo).subtract(monthRange, "month"));
  }, [monthRange, dateTo]);

  const getData = async () => {
    setIsLoading(true);
    try {
      const payload = {
        practice_id: "1002",
        date_to: dayjs(dateTo).format("MM/DD/YYYY"),
        date_from: dayjs(dateFrom).format("MM/DD/YYYY"),
      };
      const response = await getMonthlyPayerPaymentSummary(payload);
      const processedData = await processRawData(
        response,
        setBarAnnotation,
        theme
      );
      const formattedData = transformDataForGrid(response);
      console.log("formattedData: ", formattedData);

      setBarData(processedData);
      setRawData(formattedData);
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const chartHeight =
    barData.labels && barData.labels.length > 51
      ? Math.max(400, barData.labels.length * 5)
      : 600;
  const chartWidth =
    barData.labels && barData.labels.length > 51
      ? `${Math.max(200, barData.labels.length * 1.5)}vw`
      : "100vw";

  return (
    <Box height="100vh" bgcolor="white" overflow="auto">
      <Typography variant="h6" mb={2} ml={2}>
        Monthly Payer Summary Report
      </Typography>

      <Typography
        ml={2}
        variant="caption"
        fontWeight="bold"
        color="text.secondary"
        fontSize={14}
      >
        Fliters
      </Typography>

      <Box display="flex" alignItems="center" gap={2} mb={4} ml={2}>
        {/* Month Range Dropdown */}
        <Select
          value={monthRange}
          onChange={(e) => setMonthRange(e.target.value)}
          size="small"
          sx={{ minWidth: 60, mt: 2 }}
          variant="standard"
        >
          {[1, 3, 6, 9, 12].map((m) => (
            <MenuItem key={m} value={m}>
              {m} Months
            </MenuItem>
          ))}
        </Select>

        {/* Date Pickers */}
        <DesktopDatePicker
          slotProps={{ textField: { variant: "standard", label: "Date From" } }}
          format="MM/DD/YYYY"
          value={dayjs(dateFrom)}
          autoOk
          onChange={(e) => setDateFrom(e)}
          disableFuture
        />
        <DesktopDatePicker
          slotProps={{ textField: { variant: "standard", label: "Date To" } }}
          format="MM/DD/YYYY"
          value={dayjs(dateTo)}
          onChange={(e) => setDateTo(e)}
          autoOk
          disableFuture
        />

        <Button variant="contained" sx={{ mt: 2 }} onClick={getData}>
          Go
        </Button>

        {/* Toggle Show Values */}
        <FormControlLabel
          control={
            <Switch
              checked={showValues}
              onChange={() => setShowValues(!showValues)}
              size="small"
            />
          }
          label="Display Amounts"
          sx={{ mt: 2, ml: 2 }}
          disabled={showTable}
        />
        <FormControlLabel
          control={
            <Switch
              checked={showTable}
              onChange={() => setShowTable(!showTable)}
              size="small"
            />
          }
          label="Show Raw Data"
          sx={{ mt: 2 }}
        />
      </Box>
      {isLoading && (
        <Box alignItems="center" display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}
      {!isLoading && !showTable && (
        <Box
          alignItems="center"
          display="flex"
          justifyContent="center"
          overflow="auto"
          width={chartWidth}
          height={`${chartHeight}px`}
          ml={2}
        >
          <Bar data={barData} options={barOptions(barAnnotation, showValues)} />
        </Box>
      )}
      {!isLoading && showTable && (
        <Box ml={2} mr={2} mt={2}>
          <CustomDataGrid
            rows={rawData.rows}
            columns={rawData.columns}
            // getRowId={(row) => row.payer_id}
          />
        </Box>
      )}
    </Box>
  );
};

export default BillingReport;
